// src/pages/Users/Reglements/PaymentSuccess.jsx
import React, { useEffect, useState } from "react";
import { verifyPayment } from "../../../services/paymentServices";
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaTimes } from "react-icons/fa";

export default function PaymentSuccess({
  sessionId: sessionIdProp,
  onVerified,      // optionnel: callback(result) pour rafraîchir côté parent
  className = "",
}) {
  const [visible, setVisible] = useState(true);
  const [sessionId, setSessionId] = useState(sessionIdProp ?? null);
  const [status, setStatus] = useState("checking"); // "checking" | "success" | "error"
  const [message, setMessage] = useState("");

  // Récupère le session_id depuis l’URL si non fourni en prop
  useEffect(() => {
    if (sessionIdProp) return;
    const query = new URLSearchParams(window.location.search);
    const sid = query.get("session_id");
    if (sid) setSessionId(sid);
  }, [sessionIdProp]);

  // Vérifie le paiement dès qu’on a un sessionId
  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;

    const check = async () => {
      try {
        setStatus("checking");
        const res = await verifyPayment(sessionId);
        if (cancelled) return;

        const ok = !!res?.success;
        setStatus(ok ? "success" : "error");
        setMessage(
          ok
            ? (res?.message || "Paiement vérifié avec succès.")
            : (res?.message || "Erreur lors de la vérification du paiement.")
        );

        // Notifie le parent si besoin (permet de recharger la liste)
        onVerified?.(res);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setMessage("Erreur lors de la vérification du paiement.");
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [sessionId, onVerified]);

  if (!visible || !sessionId) return null;

  const isChecking = status === "checking";
  const isSuccess = status === "success";
  const isError = status === "error";

  return (
    <div className={`mx-auto w-full max-w-3xl px-3 sm:px-0 ${className}`}>
      <div
        className={[
          "relative overflow-hidden rounded-2xl shadow ring-1",
          isChecking
            ? "ring-slate-200 dark:ring-slate-700 bg-white/90 dark:bg-slate-900/90"
            : isSuccess
            ? "ring-emerald-200 dark:ring-emerald-900/50 bg-emerald-50/90 dark:bg-emerald-900/30"
            : "ring-rose-200 dark:ring-rose-900/50 bg-rose-50/90 dark:bg-rose-900/30",
        ].join(" ")}
      >
        {/* Bandeau titre */}
        <div
          className={[
            "px-4 py-3 sm:px-5 flex items-center justify-between",
            "bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white",
          ].join(" ")}
        >
          <div className="flex items-center gap-2">
            {isChecking && <FaSpinner className="animate-spin" />}
            {isSuccess && <FaCheckCircle />}
            {isError && <FaTimesCircle />}
            <h3 className="text-sm sm:text-base font-semibold">
              {isChecking && "Vérification du paiement…"}
              {isSuccess && "Paiement confirmé"}
              {isError && "Vérification du paiement"}
            </h3>
          </div>

          <button
            type="button"
            onClick={() => setVisible(false)}
            className="inline-flex items-center justify-center h-8 w-8 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-4 sm:p-5">
          {isChecking && (
            <p className="text-slate-700 dark:text-slate-200 text-sm">
              Merci pour votre achat. Votre paiement est en cours de vérification.
            </p>
          )}

          {!isChecking && (
            <p
              className={[
                "text-sm",
                isSuccess
                  ? "text-emerald-800 dark:text-emerald-200"
                  : "text-rose-800 dark:text-rose-200",
              ].join(" ")}
            >
              {message}
            </p>
          )}

          {/* Session id (facultatif / debug) */}
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 break-all">
            <span className="font-medium">Session :</span> {sessionId}
          </p>
        </div>
      </div>
    </div>
  );
}
